import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from './components/PageHeader';
import DashboardLoader from './components/DashboardLoader';
import { contactAPI } from '../../services/api';
import './ContentPage.css';

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contactAPI.list();
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      fetchMessages();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await contactAPI.delete(id);
      fetchMessages();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const filtered = messages.filter((m) => {
    if (filter === 'unread') return !m.is_read;
    if (filter === 'read') return m.is_read;
    return true;
  });

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div>
      <PageHeader
        title="Messages"
        subtitle={`${messages.length} total · ${unreadCount} unread`}
        backTo="/dashboard"
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
        }
      />

      {loading ? (
        <DashboardLoader text="Loading messages..." />
      ) : (
        <>
          {/* Filter Tabs */}
          <div className="msg-filters">
            {['all', 'unread', 'read'].map((f) => (
              <button
                key={f}
                className={`msg-filter ${filter === f ? 'msg-filter--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'unread' && unreadCount > 0 && (
                  <span className="msg-filter__badge">{unreadCount}</span>
                )}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="msg-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)' }}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
              <p>No messages found</p>
            </div>
          ) : (
            <div className="msg-list">
              {filtered.map((m) => (
                <div key={m.id} className={`msg-card ${!m.is_read ? 'msg-card--unread' : ''}`}>
                  <div className="msg-card__header">
                    <div className="msg-card__sender">
                      <div className="msg-card__avatar">
                        {(m.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="msg-card__name">{m.name}</span>
                        <span className="msg-card__email">{m.email}</span>
                      </div>
                    </div>
                    <span className="msg-card__date">
                      {new Date(m.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="msg-card__subject">{m.subject}</div>
                  <div className="msg-card__body">{m.message}</div>
                  <div className="msg-card__actions">
                    {!m.is_read && (
                      <button className="btn-action btn-action--success" style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }} onClick={() => handleMarkRead(m.id)}>
                        Mark Read
                      </button>
                    )}
                    <button className="btn-action btn-action--danger" style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }} onClick={() => handleDelete(m.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <style>{`
        .msg-filters {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }
        .msg-filter {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 1rem;
          font-size: 0.8rem;
          font-weight: 600;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          background: transparent;
          transition: all 0.2s;
          font-family: var(--font-sans);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .msg-filter:hover {
          border-color: var(--text-secondary);
        }
        .msg-filter--active {
          background: var(--accent);
          color: var(--accent-inverse);
          border-color: var(--accent);
        }
        .msg-filter__badge {
          background: rgba(239, 68, 68, 0.9);
          color: white;
          font-size: 0.65rem;
          padding: 0.1rem 0.35rem;
          border-radius: 10px;
          font-weight: 700;
        }
        .msg-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .msg-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 1.25rem;
          transition: all 0.2s;
        }
        .msg-card:hover {
          box-shadow: var(--shadow-sm);
        }
        .msg-card--unread {
          border-left: 3px solid var(--accent);
        }
        .msg-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }
        .msg-card__sender {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .msg-card__avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--bg-surface);
          color: var(--text-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-serif);
          font-weight: 700;
          font-size: 0.85rem;
          flex-shrink: 0;
        }
        .msg-card__name {
          display: block;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        .msg-card__email {
          display: block;
          font-size: 0.75rem;
          color: var(--text-muted);
        }
        .msg-card__date {
          font-size: 0.75rem;
          color: var(--text-muted);
          white-space: nowrap;
        }
        .msg-card__subject {
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 0.35rem;
        }
        .msg-card__body {
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.6;
        }
        .msg-card__actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--border-color);
        }
        .msg-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          color: var(--text-muted);
          gap: 1rem;
        }
      `}</style>
    </div>
  );
}
